import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { db } from "../../firebase";
import { downloadCsv } from "../../utils/csvExport";
import { parseEntryTimestamp } from "../../utils/customerEntryDates";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

function formatInr(n) {
  return `₹${inrFmt.format(Math.round(Number(n) || 0))}`;
}

function toMonthKey(dateObj) {
  return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
}

function monthNamesCsv(indices) {
  return indices.map((i) => MONTHS[i]).join(", ");
}

/**
 * Declining-trend math only (monthly grid still shows full year including running month).
 * - Past years: Jan–Jun vs Jul–Dec (full year closed).
 * - Current year: exclude the running calendar month, then split remaining closed months
 *   into an earlier chunk vs a later chunk (equal-ish split) and compare totals.
 */
function salesTrendExcludingRunningMonth(monthlyAmounts, selectedYear, now = new Date()) {
  const curYear = now.getFullYear();
  const runMonth = now.getMonth();

  let closed;
  if (selectedYear < curYear) {
    closed = [...Array(12)].map((_, i) => i);
  } else if (selectedYear > curYear) {
    closed = [];
  } else {
    closed = [];
    for (let m = 0; m < runMonth; m++) closed.push(m);
  }

  if (closed.length < 2) {
    return {
      h1Amount: 0,
      h2Amount: 0,
      trendPct: null,
      trendComparable: false,
      isDeclining: false,
      isGrowing: false,
      trendEarlierLabel: "",
      trendLaterLabel: "",
      trendNAReason:
        selectedYear > curYear
          ? "Future year — no trend."
          : "Need at least two completed months before the running month.",
    };
  }

  let earlierIdx;
  let laterIdx;
  if (selectedYear < curYear) {
    earlierIdx = closed.filter((m) => m < 6);
    laterIdx = closed.filter((m) => m >= 6);
  } else {
    const mid = Math.floor(closed.length / 2);
    earlierIdx = closed.slice(0, mid);
    laterIdx = closed.slice(mid);
  }

  let h1 = 0;
  for (const m of earlierIdx) h1 += monthlyAmounts[m] || 0;
  let h2 = 0;
  for (const m of laterIdx) h2 += monthlyAmounts[m] || 0;

  const trendComparable = h1 > 0;
  const trendPct = trendComparable ? ((h2 - h1) / h1) * 100 : null;
  const isDeclining = trendComparable && h2 < h1;
  const isGrowing = trendComparable && h2 > h1;

  return {
    h1Amount: h1,
    h2Amount: h2,
    trendPct,
    trendComparable,
    isDeclining,
    isGrowing,
    trendEarlierLabel: monthNamesCsv(earlierIdx),
    trendLaterLabel: monthNamesCsv(laterIdx),
    trendNAReason: trendComparable ? "" : "No sales in the earlier comparison months.",
  };
}

export default function CustomerMonthlyPurchaseReportPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [sortBy, setSortBy] = useState("yearlyAmount_desc");
  const [minYearAmount, setMinYearAmount] = useState("");
  const [minActiveMonths, setMinActiveMonths] = useState("0");
  /** all | declining | comparable — comparable = earlier trend window had sales */
  const [trendFilter, setTrendFilter] = useState("all");
  const [loadError, setLoadError] = useState("");
  const initialYearAppliedRef = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "customer_entries"),
      (snap) => {
        setDocs(snap.docs);
        setLoadError("");
        setLoading(false);
      },
      (err) => {
        console.error("customer_entries snapshot", err);
        setLoadError(err?.message || "Could not load customer entries.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const years = useMemo(() => {
    const y = new Set([new Date().getFullYear()]);
    docs.forEach((d) => {
      const dt = parseEntryTimestamp(d.data() || {});
      if (dt) y.add(dt.getFullYear());
    });
    return Array.from(y).sort((a, b) => b - a);
  }, [docs]);

  /** Once dated rows exist: default year = latest year with data (avoids empty report when UI year is ahead of your data). */
  useEffect(() => {
    if (loading || initialYearAppliedRef.current) return;
    let maxY = null;
    for (const d of docs) {
      const data = d.data() || {};
      if (data.voided === true) continue;
      const dt = parseEntryTimestamp(data);
      if (!dt) continue;
      const y = dt.getFullYear();
      maxY = maxY == null ? y : Math.max(maxY, y);
    }
    if (maxY == null) return;
    initialYearAppliedRef.current = true;
    setSelectedYear(maxY);
  }, [loading, docs]);

  const rows = useMemo(() => {
    const map = new Map();
    for (const d of docs) {
      const data = d.data() || {};
      if (data.voided === true) continue;
      const dt = parseEntryTimestamp(data);
      if (!dt) continue;
      if (dt.getFullYear() !== selectedYear) continue;

      const customerName = String(data.customerName ?? "").trim();
      if (!customerName) continue;
      const amount = Number(data.amount) || 0;
      const monthIndex = dt.getMonth();
      const monthKey = toMonthKey(dt);

      if (!map.has(customerName)) {
        map.set(customerName, {
          customerName,
          monthlyAmounts: Array.from({ length: 12 }, () => 0),
          monthlyTxnCounts: Array.from({ length: 12 }, () => 0),
          activeMonths: new Set(),
          yearlyAmount: 0,
          txnCount: 0,
          uniqueInvoiceCount: 0,
          invoiceSet: new Set(),
        });
      }

      const row = map.get(customerName);
      row.monthlyAmounts[monthIndex] += amount;
      row.monthlyTxnCounts[monthIndex] += 1;
      row.activeMonths.add(monthKey);
      row.yearlyAmount += amount;
      row.txnCount += 1;
      if (Array.isArray(data.invoiceNumbers) && data.invoiceNumbers.length) {
        data.invoiceNumbers.forEach((inv) => row.invoiceSet.add(String(inv).trim().toLowerCase()));
      } else if (data.invoiceNo) {
        String(data.invoiceNo)
          .split(",")
          .map((x) => x.trim().toLowerCase())
          .filter(Boolean)
          .forEach((inv) => row.invoiceSet.add(inv));
      }
      row.uniqueInvoiceCount = row.invoiceSet.size;
    }

    const now = new Date();
    return Array.from(map.values()).map((r) => {
      const t = salesTrendExcludingRunningMonth(r.monthlyAmounts, selectedYear, now);
      return { ...r, activeMonthCount: r.activeMonths.size, ...t };
    });
  }, [docs, selectedYear]);

  const baseFilteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const minAmt = Number(minYearAmount) || 0;
    const minMonths = Number(minActiveMonths) || 0;
    return rows.filter((r) => {
      if (q && !r.customerName.toLowerCase().includes(q)) return false;
      if (r.yearlyAmount < minAmt) return false;
      if (r.activeMonthCount < minMonths) return false;
      return true;
    });
  }, [rows, search, minYearAmount, minActiveMonths]);

  const decliningCount = useMemo(
    () => baseFilteredRows.reduce((n, r) => n + (r.isDeclining ? 1 : 0), 0),
    [baseFilteredRows]
  );

  const filteredRows = useMemo(() => {
    let list = baseFilteredRows.filter((r) => {
      if (trendFilter === "declining" && !r.isDeclining) return false;
      if (trendFilter === "comparable" && !r.trendComparable) return false;
      return true;
    });

    const trendSortKey = (r) => (r.trendPct == null ? null : r.trendPct);

    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case "trendPct_asc": {
          const na = trendSortKey(a);
          const nb = trendSortKey(b);
          if (na == null && nb == null) break;
          if (na == null) return 1;
          if (nb == null) return -1;
          if (na !== nb) return na - nb;
          break;
        }
        case "trendPct_desc": {
          const na = trendSortKey(a);
          const nb = trendSortKey(b);
          if (na == null && nb == null) break;
          if (na == null) return 1;
          if (nb == null) return -1;
          if (na !== nb) return nb - na;
          break;
        }
        case "name_asc":
          return a.customerName.localeCompare(b.customerName);
        case "name_desc":
          return b.customerName.localeCompare(a.customerName);
        case "txn_desc":
          return b.txnCount - a.txnCount;
        case "activeMonths_desc":
          return b.activeMonthCount - a.activeMonthCount;
        case "yearlyAmount_asc":
          return a.yearlyAmount - b.yearlyAmount;
        case "yearlyAmount_desc":
        default:
          return b.yearlyAmount - a.yearlyAmount;
      }
      return b.yearlyAmount - a.yearlyAmount;
    });
    return list;
  }, [baseFilteredRows, trendFilter, sortBy]);

  const totals = useMemo(() => {
    const monthlyTotals = Array.from({ length: 12 }, () => 0);
    let yearAmount = 0;
    let txnCount = 0;
    filteredRows.forEach((r) => {
      yearAmount += r.yearlyAmount;
      txnCount += r.txnCount;
      r.monthlyAmounts.forEach((n, i) => {
        monthlyTotals[i] += n;
      });
    });
    return { monthlyTotals, yearAmount, txnCount, customerCount: filteredRows.length };
  }, [filteredRows]);

  const exportCsv = useCallback(() => {
    const cols = [
      { key: "customerName", header: "Customer" },
      ...MONTHS.map((m, i) => ({ key: `m${i}`, header: m })),
      { key: "trendEarlierTotal", header: "Trend earlier total" },
      { key: "trendLaterTotal", header: "Trend later total" },
      { key: "trendPct", header: "Trend %" },
      { key: "yearlyAmount", header: "Year Total" },
      { key: "txnCount", header: "Txns" },
      { key: "uniqueInvoiceCount", header: "Invoices" },
      { key: "activeMonthCount", header: "Active Months" },
    ];
    const csvRows = filteredRows.map((r) => {
      const row = {
        customerName: r.customerName,
        trendEarlierTotal: Math.round(r.h1Amount),
        trendLaterTotal: Math.round(r.h2Amount),
        trendPct: r.trendPct == null ? "" : Math.round(r.trendPct * 10) / 10,
        yearlyAmount: Math.round(r.yearlyAmount),
        txnCount: r.txnCount,
        uniqueInvoiceCount: r.uniqueInvoiceCount,
        activeMonthCount: r.activeMonthCount,
      };
      r.monthlyAmounts.forEach((amt, i) => {
        row[`m${i}`] = Math.round(amt);
      });
      return row;
    });
    downloadCsv(`customer-monthly-purchase-${selectedYear}`, cols, csvRows);
  }, [filteredRows, selectedYear]);

  const trendHelpBlurb = useMemo(() => {
    const now = new Date();
    const cy = now.getFullYear();
    const rm = MONTHS[now.getMonth()];
    const head = `Month-wise purchase totals for ${selectedYear}. `;
    if (selectedYear < cy) {
      return `${head}Declining trend compares Jan–Jun vs Jul–Dec for that full year.`;
    }
    if (selectedYear > cy) {
      return `${head}Trend is not computed for a future year.`;
    }
    return `${head}Declining uses completed months only: ${rm} is excluded as the running month. Earlier chunk vs later chunk (split evenly across closed months); later ₹ lower than earlier ₹ counts as declining.`;
  }, [selectedYear]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-[95rem] px-2 pb-10 pt-1 sm:px-3 sm:pt-2">
        <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
          <div className="bg-gradient-to-br from-indigo-700 via-indigo-700 to-violet-900 px-3 py-2.5 text-white sm:px-4 sm:py-3">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <button
                  type="button"
                  onClick={() => navigate("/reports/customer-entry")}
                  className="mb-1 text-[11px] font-semibold text-indigo-100 hover:text-white"
                >
                  ← Back to Customer Entry
                </button>
                <h1 className="text-sm sm:text-base font-bold tracking-tight">Customer Monthly Purchase Report</h1>
                <p className="mt-0.5 max-w-2xl text-[11px] text-indigo-100/90 leading-snug">{trendHelpBlurb}</p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="rounded-lg border border-white/25 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-900 shadow-sm"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search customer..."
                  className="rounded-lg border border-white/25 bg-white px-2.5 py-1.5 text-[11px] text-slate-900 placeholder:text-slate-400 shadow-sm min-w-[180px]"
                />
                <select
                  value={trendFilter}
                  onChange={(e) => setTrendFilter(e.target.value)}
                  className="rounded-lg border border-white/25 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-900 shadow-sm"
                >
                  <option value="all">Trend: All customers</option>
                  <option value="declining">Trend: Declining only</option>
                  <option value="comparable">Trend: Comparable (earlier window had sales)</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-lg border border-white/25 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-900 shadow-sm"
                >
                  <option value="yearlyAmount_desc">Sort: Year total (high to low)</option>
                  <option value="yearlyAmount_asc">Sort: Year total (low to high)</option>
                  <option value="trendPct_asc">Sort: Trend % — worst decline first</option>
                  <option value="trendPct_desc">Sort: Trend % — strongest growth first</option>
                  <option value="txn_desc">Sort: Transactions (high to low)</option>
                  <option value="activeMonths_desc">Sort: Active months (high to low)</option>
                  <option value="name_asc">Sort: Name (A-Z)</option>
                  <option value="name_desc">Sort: Name (Z-A)</option>
                </select>
                <input
                  type="number"
                  min="0"
                  value={minYearAmount}
                  onChange={(e) => setMinYearAmount(e.target.value)}
                  placeholder="Min year amount"
                  className="rounded-lg border border-white/25 bg-white px-2.5 py-1.5 text-[11px] text-slate-900 placeholder:text-slate-400 shadow-sm w-[120px]"
                />
                <select
                  value={minActiveMonths}
                  onChange={(e) => setMinActiveMonths(e.target.value)}
                  className="rounded-lg border border-white/25 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-900 shadow-sm"
                >
                  <option value="0">All active months</option>
                  <option value="1">1+ months</option>
                  <option value="3">3+ months</option>
                  <option value="6">6+ months</option>
                  <option value="9">9+ months</option>
                  <option value="12">12 months</option>
                </select>
                <button
                  type="button"
                  disabled={filteredRows.length === 0}
                  onClick={exportCsv}
                  className="inline-flex items-center gap-1 rounded-lg border border-emerald-300/90 bg-emerald-500 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          <div className="p-2.5 sm:p-3">
            {loadError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <p className="font-semibold">Could not load report</p>
                <p className="mt-1 text-red-700/90">{loadError}</p>
              </div>
            ) : loading ? (
              <div className="py-16 text-center text-sm text-slate-500">Loading report...</div>
            ) : filteredRows.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-500">
                {trendFilter === "declining"
                  ? "No declining customers for these filters (later trend window lower than earlier; running month excluded for this year)."
                  : trendFilter === "comparable"
                    ? "No customers with a defined trend match these filters."
                    : "No records found for selected filters."}
              </div>
            ) : (
              <>
                <div className="mb-2.5 flex flex-wrap gap-2 rounded-xl border border-slate-200/90 bg-slate-50/90 px-3 py-2 text-[11px] text-slate-700">
                  <span className="font-semibold text-slate-900">{totals.customerCount}</span>
                  <span>customers</span>
                  <span className="text-slate-300">·</span>
                  <span className="font-semibold text-indigo-900">{formatInr(totals.yearAmount)}</span>
                  <span>year total</span>
                  <span className="text-slate-300">·</span>
                  <span>
                    <span className="font-semibold text-slate-900">{totals.txnCount}</span> transactions
                  </span>
                  <span className="text-slate-300">·</span>
                  <span className="text-rose-800">
                    <span className="font-semibold">{decliningCount}</span> declining (trend rule)
                  </span>
                </div>
                <div className="overflow-auto rounded-xl border border-slate-200">
                  <table className="min-w-[1320px] w-full text-xs">
                    <thead className="sticky top-0 z-[2] bg-slate-100/95 shadow-[0_1px_0_0_rgb(226_232_240)]">
                      <tr>
                        <th className="sticky left-0 top-0 z-[3] bg-slate-100 border-b border-slate-200 p-2.5 text-left font-bold text-slate-700">
                          Customer
                        </th>
                        {MONTHS.map((m) => (
                          <th
                            key={m}
                            className="sticky top-0 z-[2] bg-slate-100 border-b border-slate-200 p-2.5 text-right font-bold text-slate-700"
                          >
                            {m}
                          </th>
                        ))}
                        <th
                          title="Later trend window vs earlier (running month excluded this year; past years = Jul–Dec vs Jan–Jun)."
                          className="sticky top-0 z-[2] bg-slate-100 border-b border-slate-200 p-2.5 text-right font-bold text-slate-700"
                        >
                          Trend %
                        </th>
                        <th className="sticky top-0 z-[2] bg-slate-100 border-b border-slate-200 p-2.5 text-right font-bold text-indigo-900">
                          Year Total
                        </th>
                        <th className="sticky top-0 z-[2] bg-slate-100 border-b border-slate-200 p-2.5 text-right font-bold text-slate-700">
                          Txns
                        </th>
                        <th className="sticky top-0 z-[2] bg-slate-100 border-b border-slate-200 p-2.5 text-right font-bold text-slate-700">
                          Invoices
                        </th>
                        <th className="sticky top-0 z-[2] bg-slate-100 border-b border-slate-200 p-2.5 text-right font-bold text-slate-700">
                          Active Months
                        </th>
                      </tr>
                    </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r.customerName} className="odd:bg-white even:bg-slate-50/70">
                        <td className="sticky left-0 z-[1] bg-inherit border-b border-slate-100 p-2.5 font-semibold text-slate-900">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/reports/customer-entry/detail/${encodeURIComponent(r.customerName)}`)
                            }
                            className="max-w-[220px] truncate text-left text-indigo-700 underline decoration-indigo-300 underline-offset-2 hover:text-indigo-900"
                          >
                            {r.customerName}
                          </button>
                        </td>
                        {r.monthlyAmounts.map((n, i) => {
                          const tc = r.monthlyTxnCounts[i] || 0;
                          return (
                            <td
                              key={i}
                              title={tc > 0 ? `${tc} transaction${tc === 1 ? "" : "s"}` : undefined}
                              className="border-b border-slate-100 p-2.5 text-right tabular-nums text-slate-700"
                            >
                              {n > 0 ? formatInr(n) : "—"}
                            </td>
                          );
                        })}
                        <td className="border-b border-slate-100 p-2.5 text-right tabular-nums">
                          {r.trendComparable ? (
                            <span
                              title={`Earlier (${r.trendEarlierLabel}) ${formatInr(r.h1Amount)} · Later (${r.trendLaterLabel}) ${formatInr(r.h2Amount)}`}
                              className={
                                r.isDeclining
                                  ? "font-semibold text-rose-700"
                                  : r.isGrowing
                                    ? "font-semibold text-emerald-700"
                                    : "text-slate-600"
                              }
                            >
                              {r.trendPct === 0
                                ? "0%"
                                : `${r.trendPct > 0 ? "+" : ""}${(Math.round(r.trendPct * 10) / 10).toLocaleString("en-IN")}%`}
                            </span>
                          ) : (
                            <span className="text-slate-400" title={r.trendNAReason || "Trend not available"}>
                              —
                            </span>
                          )}
                        </td>
                        <td className="border-b border-slate-100 p-2.5 text-right font-bold tabular-nums text-indigo-800">
                          {formatInr(r.yearlyAmount)}
                        </td>
                        <td className="border-b border-slate-100 p-2.5 text-right tabular-nums text-slate-700">{r.txnCount}</td>
                        <td className="border-b border-slate-100 p-2.5 text-right tabular-nums text-slate-700">{r.uniqueInvoiceCount}</td>
                        <td className="border-b border-slate-100 p-2.5 text-right tabular-nums text-slate-700">{r.activeMonthCount}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100">
                      <th className="sticky left-0 z-[1] bg-slate-100 border-t border-slate-200 p-2.5 text-left font-bold text-slate-800">
                        Total
                      </th>
                      {totals.monthlyTotals.map((n, i) => (
                        <th key={i} className="border-t border-slate-200 p-2.5 text-right font-bold tabular-nums text-slate-800">
                          {n > 0 ? formatInr(n) : "—"}
                        </th>
                      ))}
                      <th className="border-t border-slate-200 p-2.5 text-right font-bold text-slate-500">—</th>
                      <th className="border-t border-slate-200 p-2.5 text-right font-black tabular-nums text-indigo-900">
                        {formatInr(totals.yearAmount)}
                      </th>
                      <th className="border-t border-slate-200 p-2.5 text-right font-bold tabular-nums text-slate-800">{totals.txnCount}</th>
                      <th className="border-t border-slate-200 p-2.5" />
                      <th className="border-t border-slate-200 p-2.5" />
                    </tr>
                  </tfoot>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
